import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import api from '../utils/api'

const ClinicalAlertsContext = createContext(null)

function makeKey(patientId, visitId) {
  return `${patientId || 'no_patient'}::${visitId || 'no_visit'}`
}

export function ClinicalAlertsProvider({ children }) {
  const [alertsByKey, setAlertsByKey] = useState({})
  const [loadingByKey, setLoadingByKey] = useState({})
  const [errorByKey, setErrorByKey] = useState({})

  const fetchPatientAlerts = useCallback(async ({ patientId, visitId }) => {
    if (!patientId) throw new Error('patientId is required')
    const key = makeKey(patientId, visitId)
    setLoadingByKey((p) => ({ ...p, [key]: true }))
    setErrorByKey((p) => ({ ...p, [key]: null }))
    try {
      const res = await api.get(`/clinical-alerts/patients/${patientId}/clinical-alerts`, {
        params: visitId ? { visit_id: visitId } : {}
      })
      const alerts = Array.isArray(res.data) ? res.data : []
      setAlertsByKey((p) => ({ ...p, [key]: alerts }))
      return alerts
    } catch (e) {
      const msg = e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Failed to load clinical alerts'
      setErrorByKey((p) => ({ ...p, [key]: msg }))
      throw e
    } finally {
      setLoadingByKey((p) => ({ ...p, [key]: false }))
    }
  }, [])

  const acknowledgeAlert = useCallback(async ({ patientId, alertId }) => {
    if (!patientId) throw new Error('patientId is required')
    if (!alertId) throw new Error('alertId is required')
    await api.post(`/clinical-alerts/patients/${patientId}/clinical-alerts/${alertId}/acknowledge`)
    // Callers can refresh; we do a best-effort local update across keys that contain this alert.
    setAlertsByKey((prev) => {
      const next = { ...prev }
      for (const key of Object.keys(next)) {
        const arr = next[key]
        if (!Array.isArray(arr)) continue
        next[key] = arr.map((a) => (a?._id === alertId ? { ...a, status: 'acknowledged', acknowledgedAt: new Date().toISOString() } : a))
      }
      return next
    })
  }, [])

  const resolveAlert = useCallback(async ({ patientId, alertId }) => {
    if (!patientId) throw new Error('patientId is required')
    if (!alertId) throw new Error('alertId is required')
    await api.post(`/clinical-alerts/patients/${patientId}/clinical-alerts/${alertId}/resolve`)
    setAlertsByKey((prev) => {
      const next = { ...prev }
      for (const key of Object.keys(next)) {
        const arr = next[key]
        if (!Array.isArray(arr)) continue
        next[key] = arr.map((a) => (a?._id === alertId ? { ...a, status: 'resolved', resolvedAt: new Date().toISOString() } : a))
      }
      return next
    })
  }, [])

  const value = useMemo(() => {
    return {
      alertsByKey,
      loadingByKey,
      errorByKey,
      fetchPatientAlerts,
      acknowledgeAlert,
      resolveAlert
    }
  }, [acknowledgeAlert, alertsByKey, errorByKey, fetchPatientAlerts, loadingByKey, resolveAlert])

  return <ClinicalAlertsContext.Provider value={value}>{children}</ClinicalAlertsContext.Provider>
}

export function useClinicalAlerts() {
  const ctx = useContext(ClinicalAlertsContext)
  if (!ctx) throw new Error('useClinicalAlerts must be used within ClinicalAlertsProvider')
  return ctx
}

export function usePatientClinicalAlerts({ patientId, visitId }) {
  const { alertsByKey, loadingByKey, errorByKey, fetchPatientAlerts } = useClinicalAlerts()
  const key = makeKey(patientId, visitId)
  return {
    key,
    alerts: alertsByKey[key] || [],
    loading: Boolean(loadingByKey[key]),
    error: errorByKey[key],
    refresh: () => fetchPatientAlerts({ patientId, visitId })
  }
}

